// Loans: money borrowed, paid back in fixed monthly slices. The list shows
// each borrowing; the detail shows the full repayment timetable (principal
// vs interest per month plus what's still owed). The create draft lives in
// page state so closing the drawer keeps it until the loan is created or the
// draft is discarded.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { AccountPicker } from "@/components/Pickers";
import { CardField } from "@/components/RecordCard";
import { type Loan, createLoan, listLoans } from "@/lib/api";
import { useAccountLabels } from "@/lib/labels";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "active":
      return "info";
    case "paid_off":
    case "closed":
      return "success";
    case "defaulted":
      return "danger";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  return status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/** Basis points (625 = 6.25%) rendered with both readable forms. */
function rateLabel(bp: number): string {
  return `${(bp / 100).toFixed(2)}% p.a. (${bp} bp)`;
}

function money(cents: number): string {
  return formatMoney({ cents, currency: "USD" });
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function toCents(amount: string): number | null {
  const value = Number(amount.trim());
  if (!Number.isFinite(value) || value <= 0) return null;
  const cents = Math.round(value * 100);
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

/** Calendar-month arithmetic on a `YYYY-MM-DD` string (UTC, no drift). */
function addMonths(date: string, months: number): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return null;
  const target = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1 + months, Number(match[3])),
  );
  if (Number.isNaN(target.getTime())) return null;
  return target.toISOString().slice(0, 10);
}

function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString();
}

interface LoanDraft {
  name: string;
  principal: string;
  rate: string;
  term: string;
  startDate: string;
  principalAccount: string;
  interestAccount: string;
}

function newLoanDraft(): LoanDraft {
  return {
    name: "",
    principal: "",
    rate: "",
    term: "",
    startDate: todayInput(),
    principalAccount: "",
    interestAccount: "",
  };
}

export function LoansPage({ embedded = false }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const accountLabels = useAccountLabels();
  const [loans, setLoans] = useState<Loan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<LoanDraft>(newLoanDraft);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Client-side table controls over the fetched list (the list endpoint has
  // no search/status params — filtering happens here).
  const [statusTab, setStatusTab] = useState("");
  const [search, setSearch] = useState("");

  const reload = useCallback(() => {
    listLoans()
      .then((rows) => {
        setLoans(rows);
        setError(null);
      })
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, []);

  useEffect(reload, [reload]);

  const updateDraft = (patch: Partial<LoanDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const draftDirty = Boolean(
    draft.name ||
      draft.principal ||
      draft.rate ||
      draft.term ||
      draft.principalAccount ||
      draft.interestAccount,
  );

  const clearDraft = () => {
    setDraft(newLoanDraft());
    setFormError(null);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      if (!draft.name.trim()) throw new Error("Name is required.");
      const principal_cents = toCents(draft.principal);
      if (principal_cents === null)
        throw new Error("Principal must be a positive amount.");
      const percent = Number(draft.rate);
      const interest_rate_bp = Math.round(percent * 100);
      if (!Number.isFinite(percent) || percent < 0 || interest_rate_bp < 0)
        throw new Error("Interest rate must be zero or a positive percent.");
      const term_months = Number(draft.term);
      if (!Number.isInteger(term_months) || term_months <= 0)
        throw new Error("Term must be a whole number of months.");
      if (!draft.startDate) throw new Error("Start date is required.");
      if (!draft.principalAccount || !draft.interestAccount)
        throw new Error("Pick the principal and interest accounts.");
      const created = await createLoan({
        name: draft.name.trim(),
        principal_cents,
        interest_rate_bp,
        term_months,
        start_date: draft.startDate,
        principal_account_id: draft.principalAccount,
        interest_account_id: draft.interestAccount,
      });
      clearDraft();
      setCreating(false);
      reload();
      void navigate({ to: "/loans/$loanId", params: { loanId: created.id } });
    } catch (cause) {
      setFormError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const statusCounts = (loans ?? []).reduce<Record<string, number>>(
    (counts, loan) => {
      const key = loan.status.toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
      return counts;
    },
    {},
  );
  const statusTabs = Object.keys(statusCounts).sort();

  // Client-side filter over the fetched list.
  const visibleLoans = (loans ?? [])
    .filter((loan) => !statusTab || loan.status.toLowerCase() === statusTab)
    .filter((loan) => {
      const query = search.trim().toLowerCase();
      return !query || loan.name.toLowerCase().includes(query);
    });

  // Drawer previews built from what the form already holds — no payload
  // fields are added.
  const previewBp = (() => {
    const trimmed = draft.rate.trim();
    if (!trimmed) return null;
    const percent = Number(trimmed);
    if (!Number.isFinite(percent) || percent < 0) return null;
    const bp = Math.round(percent * 100);
    return bp >= 0 ? bp : null;
  })();
  const previewTerm = (() => {
    const trimmed = draft.term.trim();
    if (!trimmed) return null;
    const months = Number(trimmed);
    return Number.isInteger(months) && months > 0 ? months : null;
  })();
  const firstDue = previewTerm !== null ? addMonths(draft.startDate, 1) : null;
  const lastDue = previewTerm !== null ? addMonths(draft.startDate, previewTerm) : null;
  const termPreview =
    previewTerm !== null && firstDue !== null && lastDue !== null
      ? `${previewTerm} monthly ${
          previewTerm === 1 ? "payment" : "payments"
        }, ${formatDate(firstDue)} → ${formatDate(lastDue)}`
      : null;

  const accountLabel = (accountId: string) =>
    accountLabels.get(accountId) ?? (
      <span title={accountId}>Unknown account</span>
    );

  const actions = (
    <Button type="button" size="sm" onClick={() => setCreating(true)}>
      New loan
    </Button>
  );

  return (
    <div className="grid gap-6">
      {embedded ? (
        <div className="actions-row">{actions}</div>
      ) : (
        <PageHeader
          title="Loans"
          description="Money borrowed and paid back in monthly slices — each loan's full repayment timetable lives on its page."
          actions={actions}
        />
      )}

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      <Tabs
        label="Filter loans by status"
        tabs={[
          { value: "", label: `All (${(loans ?? []).length})` },
          ...statusTabs.map((status) => ({
            value: status,
            label: `${plainStatus(status)} (${statusCounts[status]})`,
          })),
        ]}
        value={statusTab}
        onValueChange={setStatusTab}
      >
        <div className="surface grid gap-3 rounded-2xl p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label
              htmlFor="loans-search"
              className="grid min-w-40 flex-1 gap-1 text-xs font-medium"
            >
              Search
              <Input
                id="loans-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Loan name…"
              />
            </label>
            <Button type="button" variant="secondary" onClick={reload}>
              Refresh
            </Button>
          </div>
        </div>

        {!error && loans === null ? (
          <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
        ) : !error && loans !== null && loans.length === 0 ? (
          <EmptyState
            title="No loans yet"
            reason="When the business borrows money, track the repayment timetable here — principal vs interest per month."
          />
        ) : loans !== null ? (
          <>
            <div
              className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
              data-testid="loans-table"
            >
              <div className="table-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Loan</TableHead>
                      <TableHead className="text-right">Borrowed</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">
                        Monthly slice
                      </TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleLoans.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          No loans match these filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleLoans.map((loan) => (
                        <TableRow key={loan.id}>
                          <TableCell>
                            <Link
                              to="/loans/$loanId"
                              params={{ loanId: loan.id }}
                              className="font-medium hover:underline"
                            >
                              {loan.name}
                            </Link>
                            <span className="block text-xs text-[var(--muted-foreground)]">
                              {loan.term_months}{" "}
                              {loan.term_months === 1 ? "month" : "months"}
                            </span>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {money(loan.principal_cents)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {rateLabel(loan.interest_rate_bp)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {money(loan.monthly_payment_cents)}
                          </TableCell>
                          <TableCell>
                            <StatusDot
                              tone={statusTone(loan.status)}
                              label={plainStatus(loan.status)}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div
              className="stack-list grid gap-3 min-[64rem]:hidden"
              data-testid="loans-stack"
            >
              {visibleLoans.length === 0 ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  No loans match these filters.
                </p>
              ) : (
                visibleLoans.map((loan) => (
                  <article
                    key={loan.id}
                    className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                  >
                    <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                      <Link
                        to="/loans/$loanId"
                        params={{ loanId: loan.id }}
                        className="record-title underline-offset-4 hover:underline"
                      >
                        {loan.name}
                      </Link>
                      <span className="tabular-nums">
                        {money(loan.principal_cents)}
                      </span>
                    </div>
                    {loan.bank_account_id ? (
                      <CardField label="Bank account">
                        {accountLabel(loan.bank_account_id)}
                      </CardField>
                    ) : null}
                    <CardField label="Rate">
                      {rateLabel(loan.interest_rate_bp)}
                    </CardField>
                    <CardField label="Monthly slice">
                      {money(loan.monthly_payment_cents)}
                    </CardField>
                    <CardField label="Status">
                      <StatusDot
                        tone={statusTone(loan.status)}
                        label={plainStatus(loan.status)}
                      />
                    </CardField>
                    <div className="flex flex-wrap gap-2 pt-3">
                      <Button asChild variant="secondary" size="sm">
                        <Link
                          to="/loans/$loanId"
                          params={{ loanId: loan.id }}
                        >
                          Open loan
                        </Link>
                      </Button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </>
        ) : null}
      </Tabs>

      <Drawer
        open={creating}
        onOpenChange={(next) => {
          if (!next && !saving) setCreating(false);
        }}
        title="New loan"
        description="Builds the repayment timetable. Recording a payment later posts principal and interest. It does not send money. Closing this panel keeps your draft."
        className="w-[min(46rem,100%)]"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCreating(false)}
              disabled={saving}
            >
              Back to loans
            </Button>
            {draftDirty ? (
              <Button
                type="button"
                variant="ghost"
                onClick={clearDraft}
                disabled={saving}
              >
                Discard draft
              </Button>
            ) : null}
            <Button
              type="submit"
              form="loan-create-form"
              disabled={saving}
              className="ms-auto"
            >
              {saving ? "Creating…" : "Create loan"}
            </Button>
          </>
        }
      >
        <form
          id="loan-create-form"
          onSubmit={submit}
          className="grid gap-3"
          aria-label="Create loan"
        >
          {formError ? (
            <p role="alert" className="text-sm text-[var(--danger)]">
              {formError}
            </p>
          ) : null}
          <label htmlFor="loan-name" className="grid gap-1 text-xs font-medium">
            Name
            <Input
              id="loan-name"
              value={draft.name}
              onChange={(event) => updateDraft({ name: event.target.value })}
              required
              disabled={saving}
            />
          </label>
          <label
            htmlFor="loan-principal"
            className="grid gap-1 text-xs font-medium"
          >
            Principal (USD)
            <Input
              id="loan-principal"
              type="number"
              min="0"
              step="0.01"
              value={draft.principal}
              onChange={(event) =>
                updateDraft({ principal: event.target.value })
              }
              required
              disabled={saving}
            />
          </label>
          <label htmlFor="loan-rate" className="grid gap-1 text-xs font-medium">
            Interest rate (%)
            <Input
              id="loan-rate"
              type="number"
              min="0"
              step="0.01"
              value={draft.rate}
              onChange={(event) => updateDraft({ rate: event.target.value })}
              required
              disabled={saving}
            />
          </label>
          <label htmlFor="loan-term" className="grid gap-1 text-xs font-medium">
            Term (months)
            <Input
              id="loan-term"
              type="number"
              min="1"
              step="1"
              value={draft.term}
              onChange={(event) => updateDraft({ term: event.target.value })}
              required
              disabled={saving}
            />
          </label>
          <label htmlFor="loan-start" className="grid gap-1 text-xs font-medium">
            Starts
            <Input
              id="loan-start"
              type="date"
              value={draft.startDate}
              onChange={(event) =>
                updateDraft({ startDate: event.target.value })
              }
              required
              disabled={saving}
            />
          </label>
          {previewBp !== null || termPreview !== null ? (
            <p
              className="text-sm text-[var(--muted-foreground)]"
              aria-live="polite"
              data-testid="loan-draft-preview"
            >
              {previewBp !== null ? (
                <>
                  Interest: {(previewBp / 100).toFixed(2)}% p.a. ({previewBp}{" "}
                  bp)
                </>
              ) : null}
              {previewBp !== null && termPreview !== null ? " · " : null}
              {termPreview}
            </p>
          ) : null}
          <label
            htmlFor="loan-principal-account"
            className="grid gap-1 text-xs font-medium"
          >
            Principal account
            <AccountPicker
              id="loan-principal-account"
              value={draft.principalAccount}
              onChange={(value) => updateDraft({ principalAccount: value })}
              required
              disabled={saving}
            />
          </label>
          <label
            htmlFor="loan-interest-account"
            className="grid gap-1 text-xs font-medium"
          >
            Interest account
            <AccountPicker
              id="loan-interest-account"
              value={draft.interestAccount}
              onChange={(value) => updateDraft({ interestAccount: value })}
              required
              disabled={saving}
            />
          </label>
        </form>
      </Drawer>
    </div>
  );
}
